"use client";

import Link from "next/link";
import { useMemo } from "react";
import dynamic from "next/dynamic";
import { getImageProps } from "next/image";
import { updateImageURL } from "@/lib/utils/images";
import { getContentfulImgData } from "@/lib/services/contentful";

const RichTextRenderer = dynamic(() => import("./RichTextRenderer"));

const FeatureHeaderBanner = ({
  text,
  styles,
  heading,
  mobileBanner,
  desktopBanner,
  headingColor = "#294777",
  backgroundColor = "#EEF4FD",
}) => {
  const { desktop, mobile, rest, common } = useMemo(() => {
    const mobileImgData = getContentfulImgData(mobileBanner?.fields);
    const desktopImgData = getContentfulImgData(desktopBanner?.fields);
    const commonProps = {
      priority: true,
      alt: desktopImgData.title || desktopImgData.fileName || "banner",
    };

    const {
      props: { srcSet: desktopSet },
    } = getImageProps({
      ...commonProps,
      width: 1440,
      height: 158,
      quality: 90,
      src: updateImageURL(desktopImgData.imageUrl, 720, 525, true),
    });

    const {
      props: { srcSet: mobileSet, ...imgRest },
    } = getImageProps({
      ...commonProps,
      width: 450,
      height: 40,
      quality: 85,
      src: updateImageURL(mobileImgData.imageUrl, 734, 536, true),
    });

    return {
      mobileImg: mobileImgData,
      desktopImg: desktopImgData,
      desktop: desktopSet,
      mobile: mobileSet,
      rest: imgRest,
      common: commonProps,
    };
  }, [mobileBanner, desktopBanner]);

  const BannerImage = (
    <picture className="flex h-full w-12/12 xs:order-1 order-2 xxs:max-h-[440px] max-h-[144px] relative xs:min-h-[80px] min-h-[40px]">
      <source media="(min-width: 767px)" srcSet={desktop} />
      <source media="(max-width: 766px)" srcSet={mobile} />
      <img
        {...rest}
        alt={common.alt}
        className="object-cover w-full h-auto xs:rounded-e-[12px] xs:rounded-tl-none rounded-t-[12px]"
      />
    </picture>
  );

  return (
    <section className="common-padding-40">
      <div className="feature-card" style={{ backgroundColor }}>
        <div className="feature-card-content">
          <div className="box">
            <h2
              className={`${styles.heading32}`}
              style={{ color: headingColor }}
            >
              {heading}
            </h2>
            <div className="text-primary-black-800 mt-2">
              <RichTextRenderer content={text} />
            </div>
          </div>
        </div>
        <div className="feature-card-image">
          {desktopBanner?.fields?.link ? (
            <Link
              passHref
              prefetch={false}
              href={desktopBanner.fields.link}
              aria-label={`Navigate to ${common.alt}`}
            >
              {BannerImage}
            </Link>
          ) : (
            <div className="banner-wrapper w-full">{BannerImage}</div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeatureHeaderBanner;
